import React, { ReactNode } from 'react';
import { AvTag, AvTooltip } from '@fabric-ui/components';
import { ArrowDown, Forward, History, WidgetNoData as NoData } from '@fabric-ui/icons';
import { NoDataErrorWidget as ErrorWidget } from '@fabric-ui/icons/colorful';
import { flex } from '@fabric-ui/theme';
import { Box, Button, useTheme } from '@mui/material';
import Typography from '@mui/material/Typography';
import { iconSize } from '@fabric-ui/utils';
import * as d3 from 'd3';
import { Link } from '../../utils/AvRouter';
import { formatTimeRange } from '../../views/CustomDashboards/components/TimeFilter/utils';

const historyIcon = <History strokeWidth={0.5} style={{ height: 16, width: 16 }} />;

interface WidgetTitleProps {
  children: React.ReactNode;
  marginBottom: number;
}
export const WidgetTitle: React.FC<WidgetTitleProps> = ({ children, marginBottom }) => (
  <Box sx={{ marginBottom, fontSize: 18, fontWeight: 600 }}>{children}</Box>
);
interface WidgetDescriptionProps {
  showImage?: boolean;
  title?: ReactNode;
  description?: string;
  icon?: React.ReactNode;
}
export const NoDataFoundWidget: React.FC<WidgetDescriptionProps> = ({
  showImage = true,
  title = 'No Data Found',
  description = '',
  icon,
}) => (
  <Box sx={{ ...flex.colCenter, textAlign: 'center', flex: 1, svg: { width: 'auto', height: 'auto' } }}>
    {showImage && (icon || <NoData style={{ width: '8%', maxWidth: 68 }} />)}
    <Typography variant="h5" sx={{ color: theme => theme.palette.colors.neutrals[600], pt: 3, pb: 2 }}>
      {title}
    </Typography>
    <div>{description}</div>
  </Box>
);

interface ErrorLoadingWidgetProps {
  text?: string;
  onClick?: () => void;
}
export const ErrorLoadingWidget: React.FC<ErrorLoadingWidgetProps> = ({ text = 'Try to reload the page', onClick }) => {
  const content =
    typeof onClick === 'function' ? (
      <Button sx={{ color: theme => theme.palette.primary.main, fontWeight: 600 }} variant="text" onClick={onClick}>
        {text}
      </Button>
    ) : (
      <div>{text}</div>
    );
  return (
    <Box sx={{ ...flex.colCenter, textAlign: 'center', flex: 1, svg: { width: 'auto', height: 'auto' } }}>
      <ErrorWidget style={{ width: '8%', maxWidth: 68 }} />
      <Typography variant="h5" sx={{ color: theme => theme.palette.colors.neutrals[600], pt: 3, pb: 2 }}>
        Error Fetching Data
      </Typography>
      {content}
    </Box>
  );
};

const actionStyle = theme => ({
  ...flex.itemsCenter,
  gap: 1,
  fontWeight: 600,
  color: theme.palette.primary.main,
});

interface ExploreLinkProps {
  actionHref: string;
  title: string;
  sx?: React.CSSProperties;
}
export const ExploreLink: React.FC<ExploreLinkProps> = ({ actionHref, title, sx = {} }) => (
  <Link to={actionHref}>
    <Box sx={theme => ({ ...actionStyle(theme), ...sx })}>
      Explore {title} <Forward width={20} />
    </Box>
  </Link>
);

export const tickFormat = d3.format('.2~s');

export const getHorizontalWidth = data =>
  100 -
  Math.min(
    80,
    Math.max(
      20,
      100 -
        data
          .map(datum => datum.name)
          .reduce((max, currentString?: string) => (currentString?.length && currentString.length > max ? currentString.length : max), 0) *
          5
    )
  );

export function HistoricDataIndicator({
  hideText,
  title = 'Historical Data',
  sx = {},
}: {
  hideText?: boolean;
  title?: React.ReactNode;
  sx?;
}) {
  return (
    <AvTooltip title={hideText ? title : ''}>
      <div>
        <AvTag
          text={
            <>
              {historyIcon}
              {!hideText && title}
            </>
          }
          sx={{ ...flex.itemsCenter, gap: '6px', ...sx }}
        />
      </div>
    </AvTooltip>
  );
}

export const HistoricalIndicationWithTimeRange = ({ timeRange }) => {
  const theme = useTheme();

  return (
    <AvTooltip
      title="Displayed values are from the start or end of the selected time range (depending on the measurement) and might differ from the current value."
      popperProps={{ sx: { '.MuiTooltip-tooltip': { width: '328px', maxWidth: 'none' } } }}>
      <Box sx={{ ...flex.itemsCenter }}>
        <Typography
          variant="caption"
          sx={{
            background: theme.palette.colors.neutrals[200],
            padding: '1px 6px',
            borderTopLeftRadius: '8px',
            borderBottomLeftRadius: '8px',
            fontSize: '12px',
            textWrapMode: 'nowrap',
          }}>
          {formatTimeRange(timeRange, 'dd/MM/yy')}
        </Typography>

        <HistoricDataIndicator
          hideText
          title=""
          sx={{
            background: theme.palette.colors.primary[150],
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            padding: '3px',
          }}
        />
      </Box>
    </AvTooltip>
  );
};

export const ArrowDownIcon = (isCollapse, isFocus, theme) => (
  <ArrowDown
    style={{
      ...iconSize(16),
      transform: `rotate(${isCollapse ? 0 : -90}deg)`,
      color: isFocus ? theme.palette.colors.neutrals[600] : theme.palette.colors.neutrals[500],
    }}
  />
);
